import { createApp } from 'vue'
import { createPinia } from 'pinia'
import Antd from 'ant-design-vue'
import zhCN from 'ant-design-vue/es/locale/zh_CN'
import 'ant-design-vue/dist/reset.css'

import App from './App.vue'
import router from './router'
import { authUtils } from './api/auth'
import { clearAllAuthState, isTokenExpired, cleanupCorruptedTokens } from './utils/authUtils'

const app = createApp(App)

app.use(createPinia())
app.use(router)
app.use(Antd)

// 全局刷新状态管理
let isRefreshingToken = false
let refreshPromise: Promise<string | null> | null = null

// 导出全局刷新状态管理，供其他模块使用
export const tokenRefreshManager = {
  isRefreshing: () => isRefreshingToken,
  getRefreshPromise: () => refreshPromise,
  setRefreshing: (refreshing: boolean) => {
    isRefreshingToken = refreshing
  },
  setRefreshPromise: (promise: Promise<string | null> | null) => {
    refreshPromise = promise
  }
}

// 应用启动时的认证检查
const initializeApp = async () => {
  try {
    // 只在生产环境中清理损坏的token，开发环境中保持localStorage数据
    if (import.meta.env.PROD) {
      cleanupCorruptedTokens()
    }
    
    // 检查是否有token
    let token = localStorage.getItem('platformToken')
    const currentUser = localStorage.getItem('currentUser')
    const refreshToken = localStorage.getItem('platformToken_refresh')
    
    let tokenRefreshed = false // 标记是否已经刷新过token
    
    // 如果token不存在或已过期，但有refreshToken，尝试刷新
    if ((!token || isTokenExpiredInMain(token)) && refreshToken) {
      try {
        const newToken = await refreshTokenOnStartup(refreshToken)
        if (newToken) {
          token = newToken
          tokenRefreshed = true // 标记已经刷新过
        } else {
          // 刷新失败，清除所有token（仅在生产环境）
          if (import.meta.env.PROD) {
            clearAllTokensOnStartup()
          }
        }
      } catch (error) {
        if (import.meta.env.PROD) {
          clearAllTokensOnStartup()
        }
      }
    }
    
    // 只有在没有刷新过token且token存在的情况下，才调用authUtils.refreshTokenIfNeeded()
    if (token && currentUser && !tokenRefreshed) {
      // 尝试刷新token和用户信息
      const isValid = await authUtils.refreshTokenIfNeeded()
      if (!isValid) {
        // 清除无效的认证状态（仅在生产环境）
        if (import.meta.env.PROD) {
          clearAllTokensOnStartup()
        }
      }
    }
  } catch (error) {
    // 清除可能损坏的认证状态（仅在生产环境）
    if (import.meta.env.PROD) {
      clearAllTokensOnStartup()
    }
  }
  
  // 启动应用
  app.mount('#app')
}

// 检查token是否过期（使用统一的工具函数）
function isTokenExpiredInMain(token: string): boolean {
  return isTokenExpired(token)
}

// 在应用启动时刷新token（带防重复调用保护）
async function refreshTokenOnStartup(refreshToken: string): Promise<string | null> {
  // 如果正在刷新，返回现有的promise
  if (isRefreshingToken && refreshPromise) {
    return refreshPromise
  }
  
  // 开始刷新
  isRefreshingToken = true
  refreshPromise = performRefreshToken(refreshToken)
  
  try {
    const result = await refreshPromise
    return result
  } finally {
    // 刷新完成，重置状态
    isRefreshingToken = false
    refreshPromise = null
  }
}

// 执行实际的刷新操作
async function performRefreshToken(refreshToken: string): Promise<string | null> {
  try {
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5002'
    const apiUrl = apiBaseUrl.endsWith('/') 
      ? `${apiBaseUrl}api/auth/refresh`
      : `${apiBaseUrl}/api/auth/refresh`
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken })
    })
    
    const data = await response.json()
    
    if (data.success && data.data) {
      const newAccessToken = data.data
      localStorage.setItem('platformToken', newAccessToken)
      return newAccessToken
    } else {
      // 刷新失败时清除所有认证状态（仅在生产环境）
      if (import.meta.env.PROD) {
        clearAllTokensOnStartup()
      }
      return null
    }
  } catch (error) {
    // 刷新过程中出错时清除所有认证状态（仅在生产环境）
    if (import.meta.env.PROD) {
      clearAllTokensOnStartup()
    }
    return null
  }
}

// 清除所有token（应用启动时）
function clearAllTokensOnStartup(): void {
  // 在开发环境中，跳过清除操作以保持localStorage数据
  if (import.meta.env.DEV) {
    console.log('开发环境：跳过清除token操作，保持localStorage数据')
    return
  }
  clearAllAuthState('应用启动')
}

// 执行应用初始化
initializeApp()
